import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import {
  MatAccordion,
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as models from '@daas-consulting/security/audit/frontend/models';
import {
  AuditDetailPageViewContract,
  SecurityAuditDetailPageViewContractId,
} from '@daas-consulting/security/audit/frontend/view_contracts';
import { Observable } from 'rxjs';

@Component({
  selector: 'daas-consulting-security-audit-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDividerModule,
    MatExpansionModule,
    MatTabsModule,
    MatTooltipModule,
  ],
  templateUrl: './audit-detail-page.component.html',
  styleUrl: './audit-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditDetailPageComponent {
  @ViewChild('accordion') accordion!: MatAccordion;
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;
  @Input() entityId!: string;
  @Input() entity!: string;

  readonly item$: Observable<models.AuditModel>;
  readonly history$: Observable<models.AuditModel[]>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly detail$: Observable<any>;
  readonly hasDetail$: Observable<boolean>;
  readonly activeDetail$: Observable<number>;
  readonly #viewModel: AuditDetailPageViewContract;

  constructor(
    @Inject(SecurityAuditDetailPageViewContractId)
    viewModel: AuditDetailPageViewContract
  ) {
    this.#viewModel = viewModel;
    this.item$ = this.#viewModel.item$;
    this.history$ = this.#viewModel.items$;
    this.detail$ = this.#viewModel.detail$;
    this.hasDetail$ = this.#viewModel.hasDetail$;
    this.activeDetail$ = this.#viewModel.activeDetail$;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter(x: any): boolean {
    return x !== null && x !== undefined;
  }

  handleExpandHistory() {
    if (this._entityIdIsString() && this._entityIsString()) {
      this.#viewModel.loadHistory({
        entityId: this.entityId,
        entity: this.entity,
      });
    }
  }

  handleExpandResume() {
    if (this._entityIdIsString() && this._entityIsString()) {
      this.#viewModel.load({
        entityId: this.entityId,
        entity: this.entity,
      });
    }
  }

  handleViewDetail(item: models.AuditModel) {
    if (this._entityIdIsString() && this._entityIsString()) {
      return;
      this.#viewModel.loadDetail({
        ...item,
        entityId: this.entityId,
        entity: this.entity,
      });
    }
  }

  reset(): void {
    this.#viewModel.reset();
    if (this.accordion) {
      this.accordion.closeAll();
    }
  }

  // Métodos auxiliares para la tabla del detalle
  getObjectEntries(
    obj: Record<string, unknown>
  ): { key: string; value: unknown }[] {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  isObject(value: unknown): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  isArray(value: unknown): boolean {
    return Array.isArray(value);
  }

  getArrayLength(value: unknown): number {
    return Array.isArray(value) ? value.length : 0;
  }

  formatValue(value: unknown): string {
    if (value === null) {
      return 'null';
    }

    if (value === undefined) {
      return 'undefined';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'boolean') {
      return value.toString();
    }

    if (typeof value === 'number') {
      return value.toString();
    }

    if (value instanceof Date) {
      return value.toISOString();
    }
    return String(value);
  }

  private _entityIdIsString() {
    const rv = typeof this.entityId === 'string' && this.entityId.length > 0;
    return rv;
  }

  private _entityIsString() {
    const rv = typeof this.entity === 'string' && this.entity.length > 0;
    return rv;
  }
}
